/**
 * Form Types for Form-to-Workflow Integration
 *
 * These types support the Form Container component system that connects
 * UI forms to workflow execution via the Workflow AI service.
 */

/**
 * Form container component properties
 */
export interface FormContainerProperties {
  // Workflow binding
  workflowId?: string;
  workflowName?: string;
  schemaId?: string; // Data Service schema for validation/edit mode

  /**
   * Indicates the workflow was auto-generated during form creation
   * (vs manually created or custom workflow from AI description)
   */
  workflowAutoGenerated?: boolean;

  // Record editing (for edit mode)
  recordId?: string;
  groupId?: string;
  mode: "create" | "edit";

  /**
   * Submit mode:
   * - 'workflow' (default): Submit via workflow execution
   * - 'direct': Submit directly to Data Service (bypasses workflow)
   */
  submitMode?: 'workflow' | 'direct';

  // Layout (inherited from FlexContainer patterns)
  flexDirection: "column" | "row";
  gap: number;
  padding: number | string;

  // Behavior
  validateOnBlur: boolean;
  validateOnChange: boolean;
  resetOnSubmit: boolean;

  // Styling
  backgroundColor?: string;
  borderRadius?: number;
  border?: string;
  label?: string;
}

/**
 * Default form container properties
 */
export const DEFAULT_FORM_CONTAINER_PROPERTIES: FormContainerProperties = {
  mode: "create",
  flexDirection: "column",
  gap: 16,
  padding: 24,
  validateOnBlur: true,
  validateOnChange: false,
  resetOnSubmit: false,
  backgroundColor: "transparent",
  borderRadius: 8,
  label: "Form",
};

/**
 * Form state management
 */
export interface FormState {
  values: Record<string, unknown>;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  isSubmitting: boolean;
  isValidating: boolean;
  isValid: boolean;
  isDirty: boolean;
}

/**
 * Initial form state
 */
export const INITIAL_FORM_STATE: FormState = {
  values: {},
  errors: {},
  touched: {},
  isSubmitting: false,
  isValidating: false,
  isValid: true,
  isDirty: false,
};

/**
 * Workflow input variable schema (from workflow definition)
 */
export interface WorkflowVariable {
  name: string;
  type: "string" | "number" | "boolean" | "date" | "array" | "object";
  required: boolean;
  defaultValue?: unknown;
  description?: string;
  constraints?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    enum?: string[];
  };
}

/**
 * Complete workflow input schema
 */
export interface WorkflowInputSchema {
  workflowId: string;
  workflowName?: string;
  variables: WorkflowVariable[];
}

/**
 * Schema field definition (from Data Service)
 */
export interface SchemaField {
  name: string;
  displayName?: string;
  type: "STRING" | "NUMBER" | "BOOLEAN" | "DATE" | "DATETIME" | "ARRAY" | "OBJECT" | "REFERENCE";
  required: boolean;
  description?: string;
  defaultValue?: unknown;
  constraints?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    enum?: string[];
  };
}

/**
 * Complete schema definition (from Data Service)
 */
export interface SchemaDefinition {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  fields: SchemaField[];
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
}

/**
 * Schema list item (for listing available schemas)
 */
export interface SchemaListItem {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  fieldCount: number;
  status: string;
  groupId?: string; // Group ID for NL sources (used for context)
}

/**
 * Form submission payload (sent to workflow execution API)
 */
export interface FormSubmissionPayload {
  workflowId: string;
  triggerData: Record<string, unknown>;
  priority?: number;
  metadata?: {
    formId?: string;
    schemaId?: string;
    recordId?: string; // For edit mode
    mode?: "create" | "edit";
  };
}

/**
 * Form submission result (from workflow execution API)
 */
export interface FormSubmissionResult {
  success: boolean;
  executionId?: string;
  status?: string;
  message?: string;
  errors?: Array<{
    field?: string;
    code: string;
    message: string;
  }>;
}

/**
 * Input component form-related properties
 */
export interface InputFormProperties {
  fieldName?: string; // Maps to workflow variable or schema field
  showLabel?: boolean;
  labelText?: string;
  validationMessage?: string;
}

/**
 * Form context value (provided by FormContext)
 */
export interface FormContextValue {
  // State
  state: FormState;
  workflowSchema: WorkflowInputSchema | null;
  schemaDefinition: SchemaDefinition | null;
  isLoading: boolean;
  formId: string;
  mode: "create" | "edit";

  // Input registration
  registerInput: (fieldName: string, inputId: string) => void;
  unregisterInput: (fieldName: string) => void;

  // Value management
  setValue: (fieldName: string, value: unknown) => void;
  getValue: (fieldName: string) => unknown;
  setTouched: (fieldName: string) => void;

  // Validation
  validateField: (fieldName: string) => string | null;
  validateForm: () => Record<string, string>;
  getFieldError: (fieldName: string) => string | null;
  clearFieldError: (fieldName: string) => void;

  // Form actions
  handleSubmit: () => Promise<FormSubmissionResult>;
  resetForm: () => void;
}

/**
 * Form builder agent state (for conversational form creation)
 */
export type FormBuilderStage =
  | "idle"
  | "selecting-schema"
  | "confirming-fields"
  | "configuring-workflow"
  | "complete";

export interface FormBuilderState {
  stage: FormBuilderStage;
  availableSchemas?: SchemaListItem[];
  selectedSchema?: SchemaDefinition;
  selectedGroupId?: string; // Store groupId when schema is selected (for direct submission)
  generatedFormId?: string;
  workflowPreference?: "save-only" | "custom";
  workflowDescription?: string;
  createdWorkflowId?: string;
  error?: string;
}

/**
 * Agent response for form builder conversation
 */
export interface FormBuilderAgentResponse {
  message: string;
  expectingInput: boolean;
  inputType?:
    | "schema-selection"
    | "workflow-preference"
    | "workflow-description"
    | "field-selection";
  options?: Array<{
    id: string;
    label: string;
    description?: string;
  }>;
  error?: string;
}

/**
 * Map schema field type to HTML input type
 */
export function mapSchemaTypeToInputType(
  field: SchemaField
): string {
  // Check for enum first (select dropdown)
  if (field.constraints?.enum && field.constraints.enum.length > 0) {
    return "select";
  }

  // Check field name patterns
  const nameLower = field.name.toLowerCase();
  if (nameLower.includes("email")) return "email";
  if (nameLower.includes("password")) return "password";
  if (nameLower.includes("phone") || nameLower.includes("tel")) return "tel";
  if (nameLower.includes("url") || nameLower.includes("website")) return "url";

  // Map by type
  switch (field.type) {
    case "STRING":
      return "text";
    case "NUMBER":
      return "number";
    case "BOOLEAN":
      return "checkbox";
    case "DATE":
      return "date";
    case "DATETIME":
      return "datetime-local";
    default:
      return "text";
  }
}

/**
 * Map workflow variable type to HTML input type
 */
export function mapWorkflowTypeToInputType(
  variable: WorkflowVariable
): string {
  // Check for enum first
  if (variable.constraints?.enum && variable.constraints.enum.length > 0) {
    return "select";
  }

  // Check field name patterns
  const nameLower = variable.name.toLowerCase();
  if (nameLower.includes("email")) return "email";
  if (nameLower.includes("password")) return "password";
  if (nameLower.includes("phone") || nameLower.includes("tel")) return "tel";
  if (nameLower.includes("url") || nameLower.includes("website")) return "url";

  // Map by type
  switch (variable.type) {
    case "string":
      return "text";
    case "number":
      return "number";
    case "boolean":
      return "checkbox";
    case "date":
      return "date";
    default:
      return "text";
  }
}
